import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  IconButton,
  TablePagination,
} from "@mui/material";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import {
  DisciplinaRequestForm,
  DisciplinaRequestSchema,
} from "../../../schemas/Projetos_cadastro/Disciplina_e_SubDisciplina";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { FormSelectInput } from "../../../../../../common/components/masked_inputs/react_forms/form_select_input";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { GetDropDownChecklistResponse } from "../../../../data/daos/read/get_drop_down_checklist";
import { GetDropDownDisciplinaResponse } from "../../../../data/daos/read/get_drop_down_disciplina";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { ProjetoRevisaoResumoLista } from "../../../../domain/models/revisao_projeto_resumo_lista";
import { ProjetoRevisaoResumoPaginationStore } from "../../../hooks/Projetos_revisao_resumo/pagination";
import { GenerateRevisaoResumoColumnDefinition } from "../columns/revisaoResumo";
import { GetProjetoDisciplinaByIdResponseModel } from "../../../../domain/models/disciplina_models";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { DropDownCliente } from "../../../../../../common/components/dropdown_cliente";
import { DisciplinaLista } from "../../../../domain/models/disciplina_lista";

type Props = {
  open: boolean;
  definirDiciplinaOuSub: boolean;
  saving: boolean;
  dropDownChecklist: GetDropDownChecklistResponse[];
  dropDownDisciplina: GetDropDownDisciplinaResponse[];
  selectedDisciplina?: GetProjetoDisciplinaByIdResponseModel | null;
  revisaoLista: ProjetoRevisaoResumoLista[];
  paginationRevisao: ProjetoRevisaoResumoPaginationStore;
  disciplinaLista: DisciplinaLista[];
  onClose: () => void;
  onSubmit: (form: DisciplinaRequestForm) => void;
  onOpenRevisao: () => void;
  handleRowsPerTableChange: (rowsPerPage: number) => void;
  handleTableChange: (page: number) => Promise<void>;
  onActionEditar: (id: number | null, openState: boolean) => void;
  onActionDeletar: (id: number | null, openState: boolean) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const DisciplinaModelContent = ({
  open,
  saving,
  selectedDisciplina,
  definirDiciplinaOuSub,
  dropDownChecklist,
  dropDownDisciplina,
  revisaoLista,
  paginationRevisao,
  disciplinaLista,
  onOpenRevisao,
  onClose,
  onSubmit,
  handleTableChange,
  handleRowsPerTableChange,
  onActionDeletar,
  onActionEditar,
  onOpenChangeHistory,
}: Props) => {
  const hookForm = useForm<DisciplinaRequestForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DisciplinaRequestSchema),
  });
  const [usarDescricaoResponsavel, setUsarDescricaoResponsavel] =
    useState(false);

  useEffect(() => {
    if (open) {
      if (selectedDisciplina) {
        hookForm.setValue(
          "idDisciplina",
          selectedDisciplina.idDisciplina ?? null,
        );
        hookForm.setValue("idProjeto", null);
        hookForm.setValue(
          "idChecklist",
          selectedDisciplina.idChecklist ?? null,
        );
        hookForm.setValue("tipo", selectedDisciplina.tipo ?? null);
        hookForm.setValue("idCliente", selectedDisciplina.idCliente ?? null);
        hookForm.setValue("descricao", selectedDisciplina.descricao ?? "");
        hookForm.setValue(
          "idFuncionario",
          selectedDisciplina.idFuncionario ?? null,
        );
        hookForm.setValue(
          "idFornecedor",
          selectedDisciplina.idFornecedor ?? null,
        );
        hookForm.setValue(
          "descResponsavel",
          selectedDisciplina.descResponsavel ?? null,
        );
        hookForm.setValue(
          "datainicioPrevisto",
          selectedDisciplina.datainicioPrevisto ?? undefined,
        );
        hookForm.setValue(
          "datainicioReal",
          selectedDisciplina.datainicioReal ?? undefined,
        );
        hookForm.setValue(
          "datafimPrevisto",
          selectedDisciplina.datafimPrevisto ?? undefined,
        );
        hookForm.setValue(
          "datafimReal",
          selectedDisciplina.datafimReal ?? undefined,
        );
        hookForm.setValue(
          "dataDisciplina",
          selectedDisciplina.dataDisciplina ?? undefined,
        );
        hookForm.setValue("revisao", selectedDisciplina.revisao ?? null);
        hookForm.setValue("plantas", selectedDisciplina.plantas ?? null);
        hookForm.setValue("local1", selectedDisciplina.local1 ?? null);
        hookForm.setValue("local2", selectedDisciplina.local2 ?? null);
        hookForm.setValue("local3", selectedDisciplina.local3 ?? null);
        hookForm.setValue("local4", selectedDisciplina.local4 ?? null);
        hookForm.setValue("local5", selectedDisciplina.local5 ?? null);
        hookForm.setValue("local6", selectedDisciplina.local6 ?? null);
        hookForm.setValue("nCopia1", selectedDisciplina.nCopia1 ?? null);
        hookForm.setValue("nCopia2", selectedDisciplina.nCopia2 ?? null);
        hookForm.setValue("nCopia3", selectedDisciplina.nCopia3 ?? null);
        hookForm.setValue("nCopia4", selectedDisciplina.nCopia4 ?? null);
        hookForm.setValue("nCopia5", selectedDisciplina.nCopia5 ?? null);
        hookForm.setValue("nCopia6", selectedDisciplina.nCopia6 ?? null);
        hookForm.setValue(
          "subdiciplina",
          selectedDisciplina.idDisciplinaPai != null,
        );
        hookForm.setValue(
          "idDisciplinaPai",
          selectedDisciplina.idDisciplinaPai ?? null,
        );
        hookForm.setValue(
          "ultimaRevisao",
          selectedDisciplina.ultimaRevisao ?? null,
        );
        hookForm.setValue("idUsuario", selectedDisciplina.idUsuario ?? null);
      } else {
        hookForm.reset({
          idDisciplina: null,
          idChecklist: null,
          tipo: null,
          descricao: "",
          idFuncionario: null,
          idFornecedor: null,
          descResponsavel: null,
          datainicioPrevisto: undefined,
          datainicioReal: undefined,
          datafimPrevisto: undefined,
          datafimReal: undefined,
          dataDisciplina: undefined,
          revisao: null,
          plantas: null,
          local1: null,
          local2: null,
          local3: null,
          local4: null,
          local5: null,
          local6: null,
          subdiciplina: null,
          idDisciplinaPai: null,
          ultimaRevisao: null,
          idUsuario: null,
          nCopia1: null,
          nCopia2: null,
          nCopia3: null,
          nCopia4: null,
          nCopia5: null,
          nCopia6: null,
        });
      }
    }
  }, [open, selectedDisciplina, hookForm]);

  const columnsDefinition = useMemo(() => {
    return GenerateRevisaoResumoColumnDefinition({
      onActionDeletar: onActionDeletar,
      onActionEditar: onActionEditar,
    });
  }, [onActionDeletar, onActionEditar]);

  const tipoSelecionado = hookForm.watch("tipo");
  const idDisciplinaPaiSelecionada = hookForm.watch("idDisciplinaPai");

  useEffect(() => {
    if (definirDiciplinaOuSub && idDisciplinaPaiSelecionada) {
      const disciplinaEncontrada = disciplinaLista?.find(
        (item: any) =>
          item.IdDisciplina === idDisciplinaPaiSelecionada ||
          item.idDisciplina === idDisciplinaPaiSelecionada,
      );

      if (disciplinaEncontrada) {
        const tipoDaPai =
          disciplinaEncontrada.Tipo || disciplinaEncontrada.Tipo;
        if (tipoDaPai) {
          hookForm.setValue("tipo", tipoDaPai);
          hookForm.setValue(
            "idFuncionario",
            disciplinaEncontrada.IdFuncionario,
          );
          hookForm.setValue("idFornecedor", disciplinaEncontrada.IdFornecedor);
          hookForm.setValue("idCliente", disciplinaEncontrada.IdCliente);
        }
      }
    }
  }, [
    idDisciplinaPaiSelecionada,
    disciplinaLista,
    definirDiciplinaOuSub,
    hookForm,
  ]);

  //  Guarda na memória qual era o Tipo antes de mudar
  const previousTipo = useRef(tipoSelecionado);

  useEffect(() => {
    if (previousTipo.current && previousTipo.current !== tipoSelecionado) {
      if (tipoSelecionado === "I") {
        hookForm.setValue("idFornecedor", null);
        hookForm.setValue("idCliente", null);
      } else if (tipoSelecionado === "C") {
        hookForm.setValue("idFuncionario", null);
        hookForm.setValue("idFornecedor", null);
      } else {
        hookForm.setValue("idFuncionario", null);
        hookForm.setValue("idCliente", null);
      }
    }
    previousTipo.current = tipoSelecionado;
  }, [tipoSelecionado, hookForm]);

  const handleSubmit = useCallback(
    (form: DisciplinaRequestForm) => {
      if (!saving) onSubmit(form);
    },
    [saving, onSubmit],
  );

  const handleError = useCallback((errors: any) => {
    console.log(errors);
  }, []);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const disciplinas: GetDropDownDisciplinaResponse[] = Array.isArray(
    dropDownDisciplina,
  )
    ? dropDownDisciplina
    : (Object.values(dropDownDisciplina) as GetDropDownDisciplinaResponse[]);

  const Checklist: GetDropDownChecklistResponse[] = Array.isArray(
    dropDownChecklist,
  )
    ? dropDownChecklist
    : (Object.values(dropDownChecklist) as GetDropDownChecklistResponse[]);

  const [nADataPrevistainicio, setNADataPrevistainicio] = useState(false);
  const [nADataInicioReal, setNADataInicioReal] = useState(false);
  const [nADataFimPrevisto, setNADataFimPrevisto] = useState(false);
  const [nADataFimReal, setNADataFimReal] = useState(false);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: hookForm.handleSubmit(handleSubmit, handleError),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "62rem",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          marginBottom: 4,
        }}
      >
        {definirDiciplinaOuSub
          ? "Cadastrar Subdisciplina"
          : "Cadastrar Disciplina"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <DynamicIcon nameIcon="Close" size={24} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <ModalLoading loading={saving}>
          <Box
            component="form"
            display="flex"
            flexDirection="column"
            gap={4}
            margin={1}
          >
            <Box display="flex" flexDirection="column" gap={2.5}>
              {definirDiciplinaOuSub && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  p={1.5}
                  bgcolor="action.hover"
                  borderRadius={1}
                >
                  <FormLabel sx={{ mb: 0 }}>Última revisão?</FormLabel>
                  <FormSwitchInput
                    activeLabel="Sim"
                    inactiveLabel="Não"
                    name="ultimaRevisao"
                    readOnly={saving}
                    reactHookForm={hookForm}
                  />
                </Box>
              )}

              {definirDiciplinaOuSub && (
                <DefaultFormAutocomplete
                  name="idDisciplinaPai"
                  label="Disciplina principal."
                  readOnly={saving}
                  options={disciplinas}
                  getOptionLabel={(x) => x.descricao ?? ""}
                  getOptionKey={(x) => x.idDisciplina ?? 0}
                  reactHookForm={hookForm}
                  required={true}
                />
              )}

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <FormSelectInput
                  name="tipo"
                  label="Tipo"
                  showSelectionLabel={false}
                  options={[
                    { id: "I", label: "Interno" },
                    { id: "T", label: "Terceirizado" },
                    { id: "C", label: "Cliente" },
                  ]}
                  reactHookForm={hookForm}
                />
                <DefaultFormAutocomplete
                  name="idChecklist"
                  label="Checklist"
                  readOnly={saving}
                  options={Checklist}
                  getOptionLabel={(x) => x.checklist ?? ""}
                  getOptionKey={(x) => x.idChecklist ?? 0}
                  reactHookForm={hookForm}
                />
              </Box>

              <DefaultFormInput
                name="descricao"
                label={
                  definirDiciplinaOuSub
                    ? "Nome da prancha ou subdisciplina"
                    : "Descriçao"
                }
                required={true}
                multiline
                rows={4}
                reactHookForm={hookForm}
              />

              <Box display="flex" flexDirection="column" gap={1}>
                {usarDescricaoResponsavel ? (
                  <DefaultFormInput
                    name="descResponsavel"
                    label="Nome do Responsável Manual"
                    reactHookForm={hookForm}
                  />
                ) : tipoSelecionado === "I" ? (
                  <DropDownFuncionario
                    label="Responsável (Funcionário)"
                    name="idFuncionario"
                    reactHookForm={hookForm}
                  />
                ) : tipoSelecionado === "C" ? (
                  <DropDownCliente
                    label="Responsável (Cliente)"
                    name="idCliente"
                    reactHookForm={hookForm}
                  />
                ) : (
                  <DropDownFornecedor
                    label="Responsável (Fornecedor)"
                    name="idFornecedor"
                    reactHookForm={hookForm}
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={usarDescricaoResponsavel}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setUsarDescricaoResponsavel(checked);
                        if (checked) hookForm.setValue("idFuncionario", null);
                        else hookForm.setValue("descResponsavel", null);
                      }}
                    />
                  }
                  label={
                    <span style={{ fontSize: "0.85rem" }}>
                      Preencher responsável manualmente
                    </span>
                  }
                  sx={{ ml: 0, mt: -1 }}
                />
              </Box>
            </Box>

            <Divider />

            <Box display="flex" flexDirection="column" gap={2.5}>
              <FormLabel sx={{ mb: 0, display: "block", fontWeight: "bold" }}>
                Datas e Prazos
              </FormLabel>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box flex={1}>
                    <FormDatePickerInput
                      name="datainicioPrevisto"
                      label="Início Previsto"
                      reactHookForm={hookForm}
                      disabled={nADataPrevistainicio}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={nADataPrevistainicio}
                        onChange={(e) => {
                          setNADataPrevistainicio(e.target.checked);
                          hookForm.setValue(
                            "datainicioPrevisto",
                            e.target.checked ? null : undefined,
                          );
                        }}
                      />
                    }
                    label="NA"
                    sx={{ mr: 0 }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Box flex={1}>
                    <FormDatePickerInput
                      name="datainicioReal"
                      label="Início Real"
                      reactHookForm={hookForm}
                      disabled={nADataInicioReal}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={nADataInicioReal}
                        onChange={(e) => {
                          setNADataInicioReal(e.target.checked);
                          hookForm.setValue(
                            "datainicioReal",
                            e.target.checked ? null : undefined,
                          );
                        }}
                      />
                    }
                    label="NA"
                    sx={{ mr: 0 }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Box flex={1}>
                    <FormDatePickerInput
                      name="datafimPrevisto"
                      label="Término Previsto"
                      reactHookForm={hookForm}
                      disabled={nADataFimPrevisto}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={nADataFimPrevisto}
                        onChange={(e) => {
                          setNADataFimPrevisto(e.target.checked);
                          hookForm.setValue(
                            "datafimPrevisto",
                            e.target.checked ? null : undefined,
                          );
                        }}
                      />
                    }
                    label="NA"
                    sx={{ mr: 0 }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Box flex={1}>
                    <FormDatePickerInput
                      name="datafimReal"
                      label="Término Real"
                      reactHookForm={hookForm}
                      disabled={nADataFimReal}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={nADataFimReal}
                        onChange={(e) => {
                          setNADataFimReal(e.target.checked);
                          hookForm.setValue(
                            "datafimReal",
                            e.target.checked ? null : undefined,
                          );
                        }}
                      />
                    }
                    label="NA"
                    sx={{ mr: 0 }}
                  />
                </Box>
              </Box>

              <DefaultFormInput
                name="plantas"
                label="Pranchas / Documentos"
                reactHookForm={hookForm}
              />

              <Box display="grid" gridTemplateColumns="2fr 1fr" gap={2}>
                <DefaultFormInput
                  name="revisao"
                  label="Código da Revisão"
                  reactHookForm={hookForm}
                />
                <FormDatePickerInput
                  name="dataDisciplina"
                  label=" Data da Revisão"
                  reactHookForm={hookForm}
                />
              </Box>
            </Box>

            <Divider />
            <Box>
              <FormLabel sx={{ mb: 2, display: "block", fontWeight: "bold" }}>
                Responsável
              </FormLabel>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }}
                gap={2}
              >
                <Box display="flex" gap={1}>
                  <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
                    <DefaultFormInput
                      name="local1"
                      label="Localização 1"
                      reactHookForm={hookForm}
                    />
                  </Box>
                  <Box sx={{ width: "120px" }}>
                    <DefaultFormInput
                      name="nCopia1"
                      label="Nº Cópias"
                      reactHookForm={hookForm}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
                    <DefaultFormInput
                      name="local2"
                      label="Localização 2"
                      reactHookForm={hookForm}
                    />
                  </Box>
                  <Box sx={{ width: "120px" }}>
                    <DefaultFormInput
                      name="nCopia2"
                      label="Nº Cópias"
                      reactHookForm={hookForm}
                    />
                  </Box>
                </Box>

                <Box display="flex" gap={1}>
                  <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
                    <DefaultFormInput
                      name="local3"
                      label="Localização 3"
                      reactHookForm={hookForm}
                    />
                  </Box>
                  <Box sx={{ width: "120px" }}>
                    <DefaultFormInput
                      name="nCopia3"
                      label="Nº Cópias"
                      reactHookForm={hookForm}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
                    <DefaultFormInput
                      name="local4"
                      label="Localização 4"
                      reactHookForm={hookForm}
                    />
                  </Box>
                  <Box sx={{ width: "120px" }}>
                    <DefaultFormInput
                      name="nCopia4"
                      label="Nº Cópias"
                      reactHookForm={hookForm}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
                    <DefaultFormInput
                      name="local5"
                      label="Localização 5"
                      reactHookForm={hookForm}
                    />
                  </Box>
                  <Box sx={{ width: "120px" }}>
                    <DefaultFormInput
                      name="nCopia5"
                      label="Nº Cópias"
                      reactHookForm={hookForm}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
                    <DefaultFormInput
                      name="local6"
                      label="Localização 6"
                      reactHookForm={hookForm}
                    />
                  </Box>
                  <Box sx={{ width: "120px" }}>
                    <DefaultFormInput
                      name="nCopia6"
                      label="Nº Cópias"
                      reactHookForm={hookForm}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <div className="flex flex-grid gap-3 m-2 justify-end">
              <div className="gap-3">
                <PrimaryButton
                  startIcon={
                    <DynamicIcon
                      nameIcon="Add"
                      size={20}
                      className="text-white"
                    />
                  }
                  sx={{ px: 2, height: 40 }}
                  onClick={onOpenRevisao}
                >
                  Nova Revisão
                </PrimaryButton>
              </div>
            </div>
          </Box>
          {revisaoLista.length > 0 ? (
            <div className="h-10">
              <StyledDataGrid
                tableName="Revisões de displinas"
                disableColumnMenu={false}
                height="40vh"
                getRowId={(row: ProjetoRevisaoResumoLista) =>
                  row.idProjetoRevisaoResumo
                }
                columns={columnsDefinition}
                loading={saving}
                rows={revisaoLista}
                rowCount={paginationRevisao.totalItems}
                sx={{
                  "& .status-header": {
                    backgroundColor: "#AFDFD5",
                    color: "#000",
                  },
                }}
                pageSizeOptions={[10]}
                paginationMode="server"
                paginationModel={{
                  page: paginationRevisao.page - 1,
                  pageSize: paginationRevisao.rowsPerPage,
                }}
              />
              <TablePagination
                component="div"
                count={paginationRevisao.totalItems}
                page={paginationRevisao.page - 1}
                onPageChange={(_, p) => handleTableChange(p)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                rowsPerPage={paginationRevisao.rowsPerPage}
                onRowsPerPageChange={(e) =>
                  handleRowsPerTableChange(parseInt(e.target.value))
                }
              />
            </div>
          ) : (
            <></>
          )}
        </ModalLoading>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <div className="w-[220px]">
            {isNotNullOrUndefined(selectedDisciplina) && (
              <PrimaryButton
                buttonColor="yellow"
                onClick={() =>
                  onOpenChangeHistory(
                    Number(selectedDisciplina?.idDisciplina),
                    LogTipoTabela.DISCIPLINA,
                  )
                }
              >
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>

          <Box display="flex" gap={2} alignItems="center">
            <span
              className="text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 cursor-pointer transition-colors"
              onClick={handleClose}
            >
              Cancelar
            </span>
            <Box width="130px">
              {saving ? (
                <PrimaryButton buttonColor="primary-green" disabled>
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={hookForm.handleSubmit(handleSubmit, handleError)}
                  buttonColor="primary-green"
                >
                  Salvar
                </PrimaryButton>
              )}
            </Box>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
